import { useAuth } from "@/contexts/AuthContext";
import { getEnv } from "@/config/env";

const CONTENT_INTELLIGENCE_API_URL =
  getEnv("VITE_CONTENT_INTELLIGENCE_API_URL") ||
  "https://jca-content-intelligence.bluegrass-b6db7524.eastus.azurecontainerapps.io";

const SUB_QUESTION_PRIMING =
  "You are a helpful research assistant. When the user asks a question, generate exactly 3 relevant sub-questions that help to clarify the topic further.\n\nAlways respond with a valid JSON array of 3 strings. No preamble, no explanation, no markdown formatting, no citations — only the raw JSON array.\n";

/**
 * Calls /api/intelligence/chat to generate sub-questions for a given question.
 * Returns [] on any failure so the extraction flow proceeds gracefully.
 */
async function generateSubQuestions(
  question: string,
  model: string,
  getAccessToken: () => Promise<string | null>
): Promise<string[]> {
  try {
    const token = await getAccessToken();
    if (!token) throw new Error("Not authenticated");

    const response = await fetch(
      `${CONTENT_INTELLIGENCE_API_URL}/api/intelligence/chat`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          model,
          priming: SUB_QUESTION_PRIMING,
          messages: [{ role: "user", content: question }],
          stream: false,
        }),
      }
    );

    if (!response.ok) return [];

    const data = await response.json();

    // Response shape: { messages: [{ role: "assistant", content: "[...]" }] }
    const assistantMsg = data.messages?.find(
      (m: { role: string }) => m.role === "assistant"
    );
    if (assistantMsg?.content) {
      // The model sometimes wraps the JSON array in a markdown code block (```json ... ```)
      // Strip the code block delimiters before parsing if present
      const codeBlockMatch = /```(?:json)?\s*([\s\S]*?)```/.exec(
        assistantMsg.content as string
      );
      const jsonStr = codeBlockMatch
        ? codeBlockMatch[1].trim()
        : (assistantMsg.content as string).trim();

      const parsed = JSON.parse(jsonStr);
      if (Array.isArray(parsed)) {
        return parsed.filter((s): s is string => typeof s === "string");
      }
    }

    return [];
  } catch {
    return [];
  }
}

export function useGenerateSubQuestions() {
  const { getAccessToken } = useAuth();

  return (question: string, model: string) =>
    generateSubQuestions(question, model, getAccessToken);
}
